import { Box } from '@mui/material';
import { config } from '@converge/config';
import { useController, UseControllerProps, Control } from 'react-hook-form';
import React from 'react';

export interface ConvergeCheckboxBaseProps {
  /**
   * Whether the checkbox is checked
   */
  checked: boolean;
  /**
   * Callback function when the checkbox state changes
   */
  onChange: (checked: boolean) => void;
  /**
   * Test ID for the checkbox element
   */
  dataTestId?: string;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;
}

export const ConvergeCheckboxBase = ({
  checked,
  onChange,
  dataTestId,
  className = '',
  disabled = false,
}: ConvergeCheckboxBaseProps) => {
  const handleClick = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <Box className={className}>
      {checked && (
        <img
          data-testid={dataTestId || 'converge-checkbox-checked'}
          width="26px"
          src={config.checkBox}
          alt="checkBox"
          className="checkbox"
          onClick={handleClick}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      )}
      {!checked && (
        <img
          data-testid={dataTestId || 'converge-checkbox-unchecked'}
          width="26px"
          src={config.unselectedCheckBox}
          alt="checkBox"
          className="checkbox"
          onClick={handleClick}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      )}
    </Box>
  );
};

export type ConvergeCheckboxProps = Partial<ConvergeCheckboxBaseProps> & {
  name?: string;
  control?: Control<Record<string, unknown>>;
};

export const ConvergeCheckbox = React.forwardRef<
  HTMLDivElement,
  ConvergeCheckboxProps
>(({ name, checked, onChange, control, ...rest }, _ref) => {
  if (name && control) {
    return (
      <ConvergeCheckboxFormField
        name={name}
        control={control}
        onChange={onChange}
      />
    );
  }

  return (
    <ConvergeCheckboxBase
      checked={checked !== undefined ? checked : false}
      onChange={onChange || (() => {})}
      {...rest}
    />
  );
});

ConvergeCheckbox.displayName = 'ConvergeCheckbox';

type ConvergeCheckboxFieldFormProps = Omit<
  ConvergeCheckboxBaseProps,
  'checked' | 'onChange'
> &
  Omit<UseControllerProps, 'name'> & {
    onChange?: (checked: boolean) => void;
    name: string;
  };

export const ConvergeCheckboxFormField: React.FC<
  ConvergeCheckboxFieldFormProps
> = ({
  name,
  control,
  defaultValue,
  rules,
  onChange: externalOnChange,
  ...rest
}) => {
  const { field } = useController({
    name,
    control,
    defaultValue,
    rules,
  });

  const handleChange = (checked: boolean) => {
    // Don't allow changes if disabled
    if (rest.disabled) return;

    field.onChange(checked);
    if (externalOnChange) {
      externalOnChange(checked);
    }
  };

  return (
    <ConvergeCheckboxBase
      checked={!!field.value}
      onChange={handleChange}
      {...rest}
    />
  );
};
